import React, { createContext, ReactNode, useContext, useMemo } from "react";
import {
  CopyNamespace,
  CopyOf,
  createCopy,
  DEFAULT_LOCALE,
  Locale,
  PRODUCT_KINDS,
  ProductKind,
} from "@ifgengineering/copy-catalogue";

interface CopyScopeValue {
  locale: Locale;
  productKind: ProductKind;
}

const DEFAULT_SCOPE: CopyScopeValue = {
  locale: DEFAULT_LOCALE,
  productKind: PRODUCT_KINDS.INVESTMENT,
};

const CopyScopeContext = createContext<CopyScopeValue>(DEFAULT_SCOPE);

interface CopyScopeProps {
  productKind: ProductKind;
  children: ReactNode;
}

/** Binds every useCopy beneath it to one product. Mount it where the deal is known. */
export const CopyScope = ({
  productKind,
  children,
}: CopyScopeProps): JSX.Element => {
  const parent = useContext(CopyScopeContext);
  const value = useMemo(
    () => ({ locale: parent.locale, productKind }),
    [parent.locale, productKind]
  );
  return (
    <CopyScopeContext.Provider value={value}>
      {children}
    </CopyScopeContext.Provider>
  );
};

/**
 * The typed copy for one namespace, resolved for the enclosing CopyScope. A
 * screen that knows its deal but sits outside any scope passes the product in.
 */
export const useCopy = <N extends CopyNamespace>(
  namespace: N,
  scope?: Partial<CopyScopeValue>
): CopyOf<N> => {
  const enclosing = useContext(CopyScopeContext);
  const locale = scope?.locale ?? enclosing.locale;
  const productKind = scope?.productKind ?? enclosing.productKind;
  return useMemo(
    () => createCopy(namespace, { locale, productKind }),
    [locale, namespace, productKind]
  );
};
